import React from 'react';
import { motion } from 'framer-motion';
import { Package, MapPin, Check, Scan, CheckCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import type { PickItem } from '../../types';

interface PickItemCardProps {
  pickItem: PickItem;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onCompleteItem: (id: string) => void;
  onStartPicking: (id: string) => void;
  onScanItem: (id: string) => void;
}

const statusColors = {
  pending: 'neutral',
  picking: 'warning',
  picked: 'completed',
  short: 'warning',
  damaged: 'error'
} as const;

export const PickItemCard: React.FC<PickItemCardProps> = ({
  pickItem,
  onUpdateQuantity,
  onCompleteItem,
  onStartPicking,
  onScanItem
}) => {
  const [localQuantity, setLocalQuantity] = React.useState(pickItem.quantityPicked);
  const isComplete = pickItem.status === 'picked';
  const isInProgress = pickItem.status === 'picking' || pickItem.status === 'pending';
  const quantitiesMatch = localQuantity === pickItem.quantityRequired;
  
  // Update local quantity when pickItem changes
  React.useEffect(() => {
    setLocalQuantity(pickItem.quantityPicked);
  }, [pickItem.quantityPicked]);
  
  const handleQuantityIncrement = () => {
    const newQuantity = Math.min(localQuantity + 1, pickItem.quantityRequired + 10);
    setLocalQuantity(newQuantity);
  };
  
  const handleQuantityDecrement = () => {
    const newQuantity = Math.max(localQuantity - 1, 0);
    setLocalQuantity(newQuantity);
  };
  
  const handleCompleteItem = () => {
    onCompleteItem(pickItem.id);
  };

  return (
    <Card className="relative">
      {/* Header with product info and status */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-lg mb-1">{pickItem.productName}</h3>
          <div className="space-y-1">
            <p className="text-sm text-gray-600">
              <span className="font-medium">SKU:</span> {pickItem.productSku}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Location:</span> {pickItem.binLocation}
            </p>
          </div>
        </div>
        
        <div className="ml-4">
          <Badge variant={statusColors[pickItem.status]}>
            {pickItem.status}
          </Badge>
          {isComplete && (
            <div className="flex items-center justify-center mt-2">
              <Check className="w-6 h-6 text-green-600" />
            </div>
          )}
        </div>
      </div>
      
      {/* Quantity Controls */}
      <div className="mb-4">
        {/* Ordered/Shipped quantity display */}
        <div className="text-sm text-gray-600 mb-3 text-center">
          <span>{isComplete ? 'Shipped:' : 'Ordered:'} </span>
          <span className="font-medium text-gray-900">
            {isComplete ? pickItem.quantityPicked : pickItem.quantityRequired}
          </span>
        </div>
        
        {/* Quantity controls */}
        {!isComplete && (
          <div className="flex items-center justify-center space-x-6">
            {/* MINUS BUTTON */}
            <button
              onClick={handleQuantityDecrement}
              disabled={localQuantity <= 0}
              type="button"
              className="w-12 h-12 flex items-center justify-center bg-saavi-500 hover:bg-saavi-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors touch-manipulation"
              style={{
                fontSize: '24px',
                fontWeight: '900',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: localQuantity <= 0 ? '#9CA3AF' : '#FFFFFF'
              }}
            >
              −
            </button>
            
            {/* QUANTITY INPUT FIELD */}
            <div className="w-20">
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                value={localQuantity}
                onChange={(e) => {
                  const newQuantity = Math.max(0, parseInt(e.target.value) || 0);
                  setLocalQuantity(newQuantity);
                }}
                onFocus={(e) => e.target.select()}
                className="w-full h-12 text-center text-xl font-bold text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:border-saavi-500 focus:outline-none touch-manipulation"
                style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  textAlign: 'center'
                }}
              />
            </div>
            
            {/* PLUS BUTTON */}
            <button
              type="button"
              onClick={handleQuantityIncrement}
              className="w-12 h-12 flex items-center justify-center bg-saavi-500 hover:bg-saavi-600 rounded-lg transition-colors touch-manipulation"
              style={{
                fontSize: '24px',
                fontWeight: '900',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF'
              }}
            >
              +
            </button>
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="flex space-x-2">
        {!isComplete ? (
          <Button
            onClick={quantitiesMatch ? handleCompleteItem : undefined}
            variant={quantitiesMatch ? "scan" : "outline"}
            disabled={!quantitiesMatch}
            className="flex-1"
            icon={<CheckCircle className="w-4 h-4" />}
          >
            Complete
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => onScanItem(pickItem.id)}
            className="flex-1"
            icon={<Scan className="w-4 h-4" />}
          >
            Scan Again
          </Button>
        )}
      </div>
      
      {/* Progress indicator */}
      {isInProgress && !isComplete && (
        <div className="absolute top-2 right-2 w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
      )}
    </Card>
  );
};